import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.ts';
import { notificationsApi } from '../features/notifications/api.ts';
import { Icon } from './Icon.tsx';

type SideItem = {
  to: string;
  label: string;
  icon: string;
  matchPrefix?: string;
  showUnread?: boolean;
};

function navFor(role: 'SYS_ADMIN' | 'ORG_ADMIN' | 'USER'): SideItem[] {
  if (role === 'SYS_ADMIN') {
    return [
      { to: '/admin/orgs', label: 'Organizations', icon: 'apartment', matchPrefix: '/admin' },
      { to: '/profile',    label: 'Profile', icon: 'person' },
    ];
  }
  return [
    { to: '/dashboard', label: 'Documents',   icon: 'description', matchPrefix: '/dashboard' },
    { to: '/inbox',     label: 'Inbox',       icon: 'inbox', showUnread: true },
    { to: '/merge',     label: 'Merge PDFs',  icon: 'merge' },
    { to: '/compress',  label: 'Compress PDF', icon: 'compress' },
    ...(role === 'ORG_ADMIN' ? [{ to: '/settings', label: 'Settings', icon: 'settings', matchPrefix: '/settings' }] : []),
    { to: '/profile',   label: 'Profile',     icon: 'person' },
  ];
}

// Google-Drive-style navigation drawer (desktop): an elevated "Send a document"
// button, then full-width destination rows (icon + label) with a tonal pill
// highlight on the active row. Sits under the top app bar.
export function Sidebar(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: notifs } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list(),
    enabled: !!user,
  });
  const unread = notifs?.items.filter((i) => !i.isRead).length ?? 0;

  if (!user) return null;
  const items = navFor(user.role);
  const showNew = user.role !== 'SYS_ADMIN';

  const isActive = (item: SideItem): boolean =>
    item.to === '/dashboard'
      ? pathname.startsWith('/dashboard') || pathname.startsWith('/documents')
      : item.matchPrefix
        ? pathname.startsWith(item.matchPrefix)
        : pathname === item.to;

  const rowBase = 'flex items-center gap-4 rounded-full px-4 py-2.5 text-[0.95rem] transition-colors';

  return (
    <aside
      aria-label="Primary"
      className="hidden md:flex fixed left-0 top-16 bottom-0 z-30 w-64 flex-col gap-1 px-3 py-4 bg-[hsl(var(--chrome))] overflow-y-auto"
    >
      {showNew && (
        <Link
          to="/documents/new"
          className="mb-3 inline-flex w-fit items-center gap-3 rounded-2xl bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))] py-3.5 pl-4 pr-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <Icon name="add" size={24} />
          <span className="text-[0.95rem] font-medium">Send a document</span>
        </Link>
      )}

      <nav className="flex flex-col gap-0.5">
        {items.map((item) => {
          const active = isActive(item);
          const showBadge = item.showUnread && unread > 0;
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={active ? 'page' : undefined}
              className={[
                rowBase,
                active
                  ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] font-medium'
                  : 'text-foreground hover:bg-black/[0.05] dark:hover:bg-white/[0.06]',
              ].join(' ')}
            >
              <Icon name={item.icon} size={22} fill={active} className={active ? '' : 'text-muted-foreground'} />
              <span className="flex-1 truncate">{item.label}</span>
              {showBadge && (
                <span
                  aria-label={`${unread} unread`}
                  className="grid h-5 min-w-5 place-items-center rounded-full bg-destructive px-1.5 text-[11px] font-semibold text-destructive-foreground tabular"
                >
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Ecosystem jump */}
      <div className="mt-auto flex flex-col gap-0.5 pt-2">
        <a href="https://meetings.wapplify.app" className={`${rowBase} text-foreground hover:bg-black/[0.05] dark:hover:bg-white/[0.06]`}>
          <Icon name="calendar_month" size={22} className="text-muted-foreground" />
          <span className="flex-1 truncate">WapMeet</span>
          <Icon name="open_in_new" size={16} className="text-muted-foreground" />
        </a>
        <a href="https://office.wapplify.app" className={`${rowBase} text-foreground hover:bg-black/[0.05] dark:hover:bg-white/[0.06]`}>
          <span className="grid size-[22px] shrink-0 place-items-center">
            <svg width="20" height="20" aria-hidden="true" viewBox="0 0 48 48">
              <rect width="48" height="48" rx="13.5" fill="#006A60" />
              <path d="M9 15.5 17.5 33 24 21.5" fill="none" stroke="#9EF2E4" strokeWidth={5.6} strokeLinecap="round" strokeLinejoin="round" />
              <path d="M24 21.5 30.5 33 39 15.5" fill="none" stroke="#fff" strokeWidth={5.6} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="flex-1 truncate">Wapplify Office</span>
          <Icon name="open_in_new" size={16} className="text-muted-foreground" />
        </a>
      </div>
    </aside>
  );
}
